import * as z from 'zod';
import { contentTypeSchema } from '../enums/content.js';
import { entitlementKeySchema, featureFlagKeySchema } from '../enums/feature-flag.js';
import { collectionIdSchema, contentItemIdSchema, deviceIdSchema } from '../primitives/id.js';
import { durationSecondsSchema, instantSchema, timeZoneSchema } from '../primitives/time.js';
import { childProfileSchema, childSummarySchema } from './child.js';
/**
 * The child home screen, as a closed vocabulary.
 *
 * Deliberately shallow: no arbitrary nesting, no open component set. SPEC.md §6
 * explains why — a general schema is a large abstraction in exchange for
 * rendering four to eight tiles, and over-the-air updates already cover most of
 * what "remotely configurable" was reaching for.
 */
export const homeLayoutSchema = z.object({
    variant: z.enum(['grid2', 'grid3', 'hero_grid2']),
    sections: z.array(z.object({
        kind: z.enum(['collection', 'items']),
        title: z.string().max(40).optional(),
        itemIds: z.array(z.string().min(1)),
    })),
});
/** Enough about a collection for the child home screen to draw it. */
export const collectionSummarySchema = z.object({
    id: collectionIdSchema,
    title: z.string().min(1).max(40),
    /** Identifier for a bundled illustration; the child cannot read the title. */
    artworkKey: z.string().min(1).max(40),
    /**
     * A ready-to-load link to the channel's own mark, or null.
     *
     * A URL rather than a key because the thing that loads it is an image view,
     * which cannot be handed the API's credentials — a route behind the device
     * guard answered 401 to every tile on the tablet. The link is signed and does
     * not expire; `poster-link.ts` says why that trade is the right one here.
     *
     * Null falls back to the bundled illustration named by `artworkKey`. That
     * fallback is the exception, not the plan: the product is for a child who
     * cannot read, so the picture *is* the choice, and forty episodes wearing the
     * same bear is a screen with nothing to choose between.
     */
    posterUrl: z.url().max(500).nullable(),
    itemIds: z.array(contentItemIdSchema),
});
/**
 * Enough about a single item for a home tile to draw it.
 *
 * The approved home screen mixes collections with standalone tiles — a live
 * channel, a game — so a section of `items` needs the same title and artwork a
 * collection gets. Previously it carried only identifiers, which cannot be
 * rendered.
 *
 * The array stays empty until the catalogue milestone. The shape is settled now
 * so that interface work can follow the design board rather than wait for it.
 */
export const contentTileSchema = z.object({
    id: contentItemIdSchema,
    /**
     * The same string as `contentItemSchema.title`, and bounded the same way.
     *
     * It was capped at 40 here while the library allowed 80, and the two are the
     * same value: a title longer than forty characters made the *entire snapshot*
     * fail validation, so a device could never pair at all. One imported YouTube
     * video was enough to do it. A tile clamps its lines when a title is long —
     * that is a rendering decision, and it does not belong in the schema.
     */
    title: z.string().min(1).max(80),
    artworkKey: z.string().min(1).max(40),
    /**
     * A ready-to-load link to the item's own picture, or null.
     *
     * A URL rather than a key because the thing that loads it is an image view,
     * which cannot be handed the API's credentials — a route behind the device
     * guard answered 401 to every tile on the tablet. The link is signed and does
     * not expire; `poster-link.ts` says why that trade is the right one here.
     *
     * Null falls back to the bundled illustration named by `artworkKey`. That
     * fallback is the exception, not the plan: the product is for a child who
     * cannot read, so the picture *is* the choice, and forty episodes wearing the
     * same bear is a screen with nothing to choose between.
     */
    posterUrl: z.url().max(500).nullable(),
    contentType: contentTypeSchema,
    /**
     * How long the thing is, when that is known.
     *
     * The design board draws it as beads on the card rather than as a numeral,
     * which is the same wordless language the time meter uses — a child who
     * cannot read `4:41` can still see that one video is longer than another.
     * Null for anything with no length: a live stream, or an item whose provider
     * never told us.
     */
    durationSeconds: durationSecondsSchema.nullable(),
});
/**
 * Everything a device needs, as one atomic snapshot.
 *
 * One revision number rather than three. Earlier drafts versioned the layout,
 * the configuration and the catalogue separately without saying how they
 * related, which left "apply atomically" undefined.
 *
 * A device applies a snapshot whole or not at all. If one fails validation the
 * previous snapshot stays active and the parent is told — a child must never be
 * left with a half-applied configuration.
 */
export const configRevisionSchema = z.object({
    revision: z.int().positive(),
    updatedAt: instantSchema,
    deviceId: deviceIdSchema,
    /** Who the device is running, including what to put on screen for them. */
    child: childSummarySchema,
    /** Carried on the snapshot so the device can evaluate schedules offline. */
    timeZone: timeZoneSchema,
    profile: childProfileSchema,
    collections: z.array(collectionSummarySchema),
    /** Standalone tiles. Empty until the catalogue milestone. */
    items: z.array(contentTileSchema),
    home: homeLayoutSchema,
    featureFlags: z.partialRecord(featureFlagKeySchema, z.boolean()),
    entitlements: z.partialRecord(entitlementKeySchema, z.boolean()),
});
//# sourceMappingURL=config.js.map